import React from "react";
import {
  atomFamily,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { FabricStateProperty, ValidFabricStateTypes } from "../models/state";

export const FabricStateAtomFamily = atomFamily<any, string>({
  key: "FabricStateAtomFamily",
  default: undefined,
});

export type FabricStateSimpleUpdater<EntityType extends ValidFabricStateTypes> =
  (value: EntityType) => void;

/**
 * Accesses a piece of state stored in the container.
 * If the value has not yet been defined, it will be set to the default value.
 *
 * This returns the value and a function that sets the value. Only use this if
 * you need both. Otherwise, useFabricValue and useSetFabricValue are better-optimized to control re-renders.
 *
 * You can store anything but functions here.
 *
 * By default, this will infer its type from the stateProperty's `default` value.
 * If you need a union type, such as `string | null`, then you will need to provide that yourself, preferably on your FabricStateProperty object:
 * @example
 * const stringFabricStateProperty: FabricStateProperty<string | null> = {
 *  key: "MyApp.StringFabricStateProperty",
 *  default: "foo",
 * }
 * @throws if the `key` property of the stateProperty param doesn't match the required formatting.
 *
 */
export const useFabricState = <EntityType extends ValidFabricStateTypes>(
  stateProperty: FabricStateProperty<EntityType>,
): [EntityType, FabricStateSimpleUpdater<EntityType>] => {
  const { key, default: defaultValue } = stateProperty;
  const [_value, _setValue] = useRecoilState<EntityType>(
    FabricStateAtomFamily(key),
  );

  const shouldUseDefaultValue = React.useMemo(
    () => defaultValue !== undefined && _value === undefined,
    [defaultValue, _value],
  );

  // Why not return _setValue instead? Because `shouldUseDefaultValue` means that the state we are returning
  // from here will not always match that which is passed to the updater function of _setValue. Therefore,
  // we cannot allow that to be used without the possibility of erroneous behavior.
  const setValue = React.useCallback(
    (x: EntityType) => {
      // We use the "updater" function syntax to prevent erroneous behavior if x is a function itself.
      _setValue(() => x);
    },
    [_setValue],
  );

  /**
   * We should never return undefined if the client doesn't expect it,
   * so while the value is undefined, we return the defaultValue.
   */
  const value = React.useMemo(
    () => (shouldUseDefaultValue ? defaultValue : _value),
    [defaultValue, shouldUseDefaultValue, _value],
  );
  return React.useMemo(() => [value, setValue], [setValue, value]);
};

/**
 * Returns a value for the matching property in the Fabric state provider.
 * Use this if you only need to get the value, as it is optimized to cause fewer re-renders.
 *
 * By default, this will infer its type from the stateProperty's `default` value.
 * If you need a union type, such as `string | null`, then you will need to provide that yourself, preferably on your FabricStateProperty object:
 * @example
 * const stringFabricStateProperty: FabricStateProperty<string> = {
 *  key: "MyApp.StringFabricStateProperty",
 *  default: "foo",
 * }
 * @throws if the `key` property of the stateProperty param doesn't match the required formatting.
 */
export const useFabricValue = <EntityType extends ValidFabricStateTypes>(
  stateProperty: FabricStateProperty<EntityType>,
): EntityType => {
  const { key, default: defaultValue } = stateProperty;
  const value = useRecoilValue(FabricStateAtomFamily(key));

  const shouldUseDefaultValue = React.useMemo(
    () => defaultValue !== undefined && value === undefined,
    [defaultValue, value],
  );

  /**
   * We should never return undefined if the client doesn't expect it,
   * so while the value is undefined, we return the defaultValue.
   */
  return React.useMemo(
    () => (shouldUseDefaultValue ? defaultValue : value),
    [defaultValue, shouldUseDefaultValue, value],
  );
};

/**
 * Returns a setter for the matching property in the Fabric state provider.
 * Use this if you only need to set the value, as it is optimized to cause fewer re-renders.
 *
 * You can store anything but functions here.
 *
 * By default, this will infer its type from the stateProperty's `default` value. If you need a union type, such as `string | null`, then you will need to provide that yourself, preferably on your FabricStateProperty object:
 * @example
 * const stringFabricStateProperty: FabricStateProperty<string> = {
 *  key: "MyApp.StringFabricStateProperty",
 *  default: "foo",
 * }
 *
 * @throws if the `key` property of the stateProperty param doesn't match the formatting rules
 */
export const useSetFabricState = <EntityType extends ValidFabricStateTypes>({
  key,
}: FabricStateProperty<EntityType>): FabricStateSimpleUpdater<EntityType> => {
  const setValue = useSetRecoilState(FabricStateAtomFamily(key));
  return React.useCallback(
    (x: EntityType) => {
      // We use the "updater" function syntax to prevent erroneous behavior if x is a function itself.
      setValue(() => x);
    },
    [setValue],
  );
};
